import {
  areYouMakingTheUpdateOnBehalfOfAnotherCompanyOrHousingCompanyMsg,
  areYouTheInvoicingContactPersonMsg,
  billingEOperatorChangesMsg,
  businessIdMsg,
  companyNameMsg,
  coverageInterruptionsIncidentsMsg,
  customerNumberMsg,
  deliveryMethodMsg,
  doesTheChangeApplyToAllBillingAccountsForThisBusinessIDMsg,
  eInvoicingAddressMsg,
  eInvoicingOperatorMsg,
  emailAddressMsg,
  emailMsg,
  firstNameMsg,
  invoiceLanguageMsg,
  invoicingAndCustomerDetailsMsg,
  invoicingDetailsMsg,
  lastNameMsg,
  myServicesMsg,
  newOrdersAndOfferRequestsMsg,
  numberOfTheBillingAccountMsg,
  openOrdersMsg,
  otherTopicMsg,
  payerDetailsMsg,
  payerMsg,
  phoneNumberMsg,
  postOfficeMsg,
  postalAddressMsg,
  postalCodeMsg,
  pukCodeMsg,
  selectEffectiveDateMsg,
  t,
  technicalAdviceMsg,
  terminationMsg,
  willPayersNameOrAddressChangeMsg,
  willTheChangeTakeEffectImmediatelyMsg,
} from '../../common/i18n/index.js';
import { getStoredParametersCookie } from '../../common/utils/gaTrackingUtils.js';
import { getLanguageDisplayValue } from '../../common/utils/languageUtils.js';
import { Lead } from '../../generated/api/lead.js';
import { SupportCase } from '../../generated/api/supportCase';
import type { ContactSupportFormValues } from './ContactSupportForm.js';
import type { EOperatorChangeRequestFormValues } from './EOperatorChange/EOperatorChangeRequestForm.js';
import type { ContactSupportInfo } from './types.js';
import type { PostAnonymousLeadRequest } from '../../generated/api/postAnonymousLeadRequest.js';
import type { PostLeadRequest } from '../../generated/api/postLeadRequest.js';

/**
 * Convert ContactSupportFormValues to LeadContactDetails
 * Convert ContactSupportFormValues to SupportCaseContactDetails
 */
export const mapContactSupportFormValuesToContactDetails = ({
  businessId,
  businessName,
  firstName,
  lastName,
  email,
  phoneNumber,
}: ContactSupportFormValues) => ({
  businessId,
  businessName,
  firstName,
  lastName,
  phoneNumber,
  email,
});

export const getEOperatorChangeRequestFormValuesAsSingleString = (
  values: EOperatorChangeRequestFormValues,
  contactValues: ContactSupportInfo
) => {
  const getTranslatedBooleanValue = (value: boolean) => (value ? t.M03S('Yes') : t.A37D('No'));

  return `${t.CACT(willTheChangeTakeEffectImmediatelyMsg)}: ${getTranslatedBooleanValue(
    values.willChangeTakeEffectImmediately
  )}\n${
    !values.willChangeTakeEffectImmediately ? `${t.NDRY(selectEffectiveDateMsg)}: ${values.effectiveDate}\n` : ''
  }${t.A0OK(areYouMakingTheUpdateOnBehalfOfAnotherCompanyOrHousingCompanyMsg)}: ${getTranslatedBooleanValue(
    values.actingOnBehalfOfCompany
  )}\n${
    values.actingOnBehalfOfCompany
      ? `${t.AJ93(companyNameMsg)}: ${values.actingOnBehalfOfCompanyBusinessName}\n${t.MPA5(businessIdMsg)}: ${
          values.actingOnBehalfOfCompanyBusinessId
        }\n${t.A00L(customerNumberMsg)}: ${values.actingOnBehalfOfCompanyCustomerNumber}\n`
      : ''
  }${t.V5EW(doesTheChangeApplyToAllBillingAccountsForThisBusinessIDMsg)}: ${getTranslatedBooleanValue(
    values.allBillingAccountsApply
  )}\n${
    !values.allBillingAccountsApply
      ? `${t.GQLW(numberOfTheBillingAccountMsg)}: ${values.billingAccounts
          ?.map(ba => ba.billingAccountId)
          .join(', ')}\n`
      : ''
  }${contactValues.firstName ? `${t.AIK7(firstNameMsg)}: ${contactValues.firstName}\n` : ''}${
    contactValues.lastName ? `${t.Y41S(lastNameMsg)}: ${contactValues.lastName}\n` : ''
  }${contactValues.email ? `${t.OKYY(emailAddressMsg)}: ${contactValues.email}\n` : ''}${
    contactValues.phoneNumber ? `${t.AGIJ(phoneNumberMsg)}: ${contactValues.phoneNumber}\n` : ''
  }${t.RPMR(invoicingDetailsMsg)}\n${
    values.customerNumber ? `${t.A00L(customerNumberMsg)}: ${values.customerNumber}\n` : ''
  }${t.G0QN(deliveryMethodMsg)}: ${values.deliveryMethod}\n${t.A7DR(invoiceLanguageMsg)}: ${getLanguageDisplayValue(
    values.billLanguage
  )}\n${t.WVLB(eInvoicingOperatorMsg)}: ${values.eOperator}\n${t.OL7B(eInvoicingAddressMsg)}: ${
    values.eInvoiceAddress
  }\n${t.P9D3(areYouTheInvoicingContactPersonMsg)}: ${getTranslatedBooleanValue(values.isBillingContactPerson)}\n${
    !values.isBillingContactPerson
      ? `${t.IHRV('Invoicing contact person details')}\n${t.AIK7(firstNameMsg)}: ${
          values.billingContactPersonFirstName
        }\n${t.Y41S(lastNameMsg)}: ${values.billingContactPersonLastName}\n${t.ZVQ5(emailMsg)}: ${
          values.billingContactPersonEmail
        }\n${t.AGIJ(phoneNumberMsg)}: ${values.billingContactPersonPhone}\n`
      : ''
  }${t.GMMI(willPayersNameOrAddressChangeMsg)}: ${getTranslatedBooleanValue(values.payerNameOrAddressChange)}\n${
    values.payerNameOrAddressChange
      ? `${t.PB6S(payerMsg)}: ${values.payerName}\n${
          values.payerAdditionalInfo ? `${t.YLAI(payerDetailsMsg)}: ${values.payerAdditionalInfo}\n` : ''
        }${t.NDWK(postalAddressMsg)}: ${values.payerPostalAddress}\n${t.RUAW(postalCodeMsg)}: ${
          values.payerPostalCode
        }\n${t.Z7S5(postOfficeMsg)}: ${values.payerPostOffice}\n`
      : ''
  }`.slice(0, -1);
};

export const getFeatureTranslations = (): Partial<Record<SupportCase.FeatureEnum, string>> => ({
  [SupportCase.FeatureEnum.CONTACT_INFO_OFFER_REQUEST]: t.J923(newOrdersAndOfferRequestsMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_OPEN_ORDERS]: t.NC0D(openOrdersMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_BILLING]: t.WWSI(invoicingAndCustomerDetailsMsg),
  [SupportCase.FeatureEnum.BILLING_E_OPERATOR_CHANGES]: t.TZYQ(billingEOperatorChangesMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_SERVICES]: t.CG4F(myServicesMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_TECHNICAL]: t.V7EA(technicalAdviceMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_COVERAGE]: t.DKUZ(coverageInterruptionsIncidentsMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_CANCEL]: t.MN35(terminationMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_PUK]: t.K5TL(pukCodeMsg),
  [SupportCase.FeatureEnum.CONTACT_INFO_OTHER]: t.CHWD(otherTopicMsg),
});

export const getFeatureTranslation = (feature?: SupportCase.FeatureEnum) => {
  if (feature) {
    return getFeatureTranslations()[feature];
  }

  return '';
};

export const createAnonymousCaseDescription = (formValues: ContactSupportFormValues): string => {
  const description = formValues.extraField
    ? `${formValues.description}\n\n${formValues.extraField}`
    : formValues.description;

  return `Kategoria: ${getFeatureTranslation(formValues.feature)}
Yrityksen nimi: ${formValues.businessName}
Y-tunnus: ${formValues.businessId}
Yhteyshenkilö: ${formValues.firstName} ${formValues.lastName}
Sähköposti: ${formValues.email}
Puhelin: ${formValues.phoneNumber}

Viesti:
${description}`;
};

const buildLeadFields = (description: string, sourceUrl: string, campaignId?: string): Lead => {
  const storedParametersCookie = getStoredParametersCookie();
  return {
    leadType: Lead.LeadTypeEnum.ONLINE_CONTACT,
    sourceUrl,
    description,
    consentToContact: true,
    consentForMarketing: false,
    ...(campaignId ? { campaignId, interestArea: Lead.InterestAreaEnum.CAMPAIGN_DEFINED_AREA } : {}),
    ...(storedParametersCookie ?? {}),
  };
};

export const mapFormValuesToAnonymousLeadRequest = (
  formValues: ContactSupportFormValues,
  sourceUrl: string,
  campaignId?: string
): PostAnonymousLeadRequest => ({
  contactDetails: mapContactSupportFormValuesToContactDetails(formValues),
  lead: buildLeadFields(formValues.description, sourceUrl, campaignId),
});

export const mapFormValuesToAuthenticatedLeadRequest = (
  formValues: ContactSupportFormValues,
  contactId: string,
  sourceUrl: string,
  campaignId?: string
): PostLeadRequest => {
  const description = formValues.extraField
    ? `${formValues.description}\n\n${formValues.extraField}`
    : formValues.description;
  return {
    contactId,
    lead: buildLeadFields(description, sourceUrl, campaignId),
  };
};
